/**
 * Trade-specific job type presets.
 * When a workspace selects a trade, these presets are seeded into the job_types table.
 *
 * ~60 trades organised into 8 categories. Each trade maps to a "preset family"
 * that determines which job types, quote templates, and invoice templates get seeded.
 */

import type { GasCertType } from './gas-certs/types';

export interface JobTypePreset {
  name: string;
  category: string;
  defaultDuration: string;
  /** Lucide icon name from JOB_TYPE_ICONS (resolved via iconForJobType()).
   *  Stored verbatim in the legacy job_types.color column. */
  color: string;
  /** The gas certificate this job type produces, seeded into
   *  job_types.default_cert_type. Drives the "start the certificate" prompt on
   *  job detail and the "Certificates due" section on the gas certs page.
   *  Only meaningful for accounts running the gas-certificates skill; harmless
   *  everywhere else. Omit for job types that produce no certificate. */
  defaultCertType?: GasCertType;
}

export interface TradeDefinition {
  label: string;
  types: JobTypePreset[];
}

/**
 * Universal pre-quote survey visit — appended to EVERY family's job-type seed
 * (see seedJobTypes) and backfilled to existing accounts by migration. The
 * stable name is load-bearing: the proactive quoting engine recognises quote
 * visits via isQuoteVisit(), and this preset is what makes new bookings
 * recognisable instead of free-text guesswork.
 */
export const QUOTE_VISIT_PRESET: JobTypePreset = {
  name: 'Quote Visit',
  category: 'Inspection',
  defaultDuration: '1 hour',
  color: 'briefcase',
};

// ---------------------------------------------------------------------------
// Categorised trade list — ~60 trades in 10 categories
// ---------------------------------------------------------------------------

/**
 * 🚨 The ORDER of these categories is the order every trade picker renders in,
 * and it is deliberate rather than alphabetical.
 *
 * Heating & Plumbing and HVAC & Renewables are the only two categories signup
 * admits at all (`ELIGIBLE_TRADE_CATEGORIES` in src/lib/signup/trade-gate.ts),
 * so they are who Mucka actually sells to — putting them first means the
 * overwhelming majority of owners find their trade without scrolling, and the
 * settings picker's SECOND trade is nearly always in one of the first three
 * groups too. Electrical follows because a heating firm's most common sideline
 * is electrical work.
 *
 * Nothing reads these by position — `trade-gate.ts` sorts by its OWN declared
 * order precisely so this list can be re-ordered for the reader without moving
 * anything in the funnel — so a future re-order is a display decision only.
 * `__tests__/category-order.test.ts` pins it, because "why is Plumber below
 * Scaffolder?" is a question nobody would think to ask a diff.
 */
export const TRADE_CATEGORIES: Record<string, { value: string; label: string }[]> = {
  'Heating & Plumbing': [
    { value: 'gas-engineer', label: 'Gas Engineer' },
    { value: 'heating-engineer', label: 'Heating Engineer' },
    { value: 'boiler-installer', label: 'Boiler Installer' },
    { value: 'plumber', label: 'Plumber' },
    { value: 'bathroom-fitter', label: 'Bathroom Fitter' },
    { value: 'wet-room', label: 'Wet Room Specialist' },
    { value: 'drainage', label: 'Drainage Engineer' },
    { value: 'underfloor-heating', label: 'Underfloor Heating Installer' },
  ],
  'HVAC & Renewables': [
    { value: 'hvac', label: 'HVAC Engineer' },
    { value: 'air-con', label: 'Air Conditioning Engineer' },
    { value: 'heat-pump', label: 'Heat Pump Installer' },
    { value: 'ventilation', label: 'Ventilation Specialist' },
    { value: 'renewable-energy', label: 'Renewable Energy Installer' },
  ],
  'Electrical & Mechanical': [
    { value: 'electrician', label: 'Electrician' },
    { value: 'electrical-tester', label: 'Electrical Tester / Inspector' },
    { value: 'fire-alarm', label: 'Fire Alarm Engineer' },
    { value: 'data-cabling', label: 'Data Cabling / Networking' },
    { value: 'cctv-installer', label: 'CCTV Installer' },
    { value: 'ev-charger', label: 'EV Charger Installer' },
    { value: 'solar-pv', label: 'Solar PV Installer' },
  ],
  'General Building & Construction': [
    { value: 'general-builder', label: 'General Builder' },
    { value: 'bricklayer', label: 'Bricklayer' },
    { value: 'groundworker', label: 'Groundworker' },
    { value: 'extension-specialist', label: 'Extension Specialist' },
    { value: 'loft-conversion', label: 'Loft Conversion Specialist' },
    { value: 'demolition', label: 'Demolition Contractor' },
    { value: 'scaffolder', label: 'Scaffolder' },
    { value: 'stonemason', label: 'Stonemason' },
  ],
  'Joinery & Carpentry': [
    { value: 'carpenter', label: 'Carpenter' },
    { value: 'joiner', label: 'Joiner' },
    { value: 'kitchen-fitter', label: 'Kitchen Fitter' },
    { value: 'cabinet-maker', label: 'Cabinet Maker' },
    { value: 'shopfitter', label: 'Shopfitter' },
    { value: 'timber-frame', label: 'Timber Frame Erector' },
  ],
  'Finishing & Interiors': [
    { value: 'painter-decorator', label: 'Painter & Decorator' },
    { value: 'plasterer', label: 'Plasterer' },
    { value: 'tiler', label: 'Tiler' },
    { value: 'floor-layer', label: 'Floor Layer' },
    { value: 'dryliner', label: 'Dryliner' },
    { value: 'screeder', label: 'Screeder' },
  ],
  'Roofing & Exteriors': [
    { value: 'roofer', label: 'Roofer' },
    { value: 'flat-roofer', label: 'Flat Roofer' },
    { value: 'glazier', label: 'Glazier' },
    { value: 'window-fitter', label: 'Window & Door Fitter' },
    { value: 'fascia-soffit', label: 'Fascia & Soffit Installer' },
    { value: 'gutter-specialist', label: 'Gutter Specialist' },
    { value: 'cladding', label: 'Cladding Installer' },
    { value: 'conservatory', label: 'Conservatory Installer' },
  ],
  'Landscaping & External': [
    { value: 'landscaper', label: 'Landscaper' },
    { value: 'fencer', label: 'Fencer' },
    { value: 'decking', label: 'Decking Specialist' },
    { value: 'driveway', label: 'Driveway / Paving Contractor' },
    { value: 'tree-surgeon', label: 'Tree Surgeon' },
    { value: 'exterior-cleaner', label: 'Exterior Cleaner' },
  ],
  'Security & Home Technology': [
    { value: 'locksmith', label: 'Locksmith' },
    { value: 'aerial-satellite', label: 'Aerial & Satellite Engineer' },
    { value: 'smart-home', label: 'Smart Home Installer' },
    { value: 'home-cinema', label: 'Home Cinema Installer' },
  ],
  'Specialist & Niche': [
    { value: 'asbestos', label: 'Asbestos Removal' },
    { value: 'pest-control', label: 'Pest Control' },
    { value: 'chimney-sweep', label: 'Chimney Sweep' },
    { value: 'domestic-cleaner', label: 'Domestic Cleaner' },
    { value: 'commercial-cleaner', label: 'Commercial Cleaner' },
    { value: 'swimming-pool', label: 'Swimming Pool Engineer' },
    { value: 'steel-fixer', label: 'Steel Fixer' },
    { value: 'piling', label: 'Piling Contractor' },
    { value: 'handyman', label: 'Handyman' },
  ],
};

// Flat list for dropdowns
export const TRADE_OPTIONS: { value: string; label: string }[] =
  Object.values(TRADE_CATEGORIES).flat();

// ---------------------------------------------------------------------------
// Map each trade value → preset family key for seeding
// ---------------------------------------------------------------------------

export type PresetFamilyKey =
  | 'gas' | 'plumbing' | 'electrical' | 'hvac' | 'general'
  | 'solar-ev' | 'building' | 'roofing' | 'joinery' | 'finishing'
  | 'glazing' | 'landscaping' | 'demolition' | 'security-av' | 'niche'
  | 'exterior-cleaning' | 'domestic-cleaning' | 'commercial-cleaning';

export const TRADE_PRESET_MAP: Record<string, PresetFamilyKey> = {
  // Backward compat — old values
  'gas': 'gas',
  'plumbing': 'plumbing',
  'electrical': 'electrical',
  'hvac': 'hvac',
  'general': 'general',

  // General Building & Construction
  'general-builder': 'building',
  'bricklayer': 'building',
  'groundworker': 'building',
  'extension-specialist': 'building',
  'loft-conversion': 'building',
  'demolition': 'demolition',
  'scaffolder': 'demolition',
  'stonemason': 'building',

  // Electrical & Mechanical
  'electrician': 'electrical',
  'electrical-tester': 'electrical',
  'fire-alarm': 'electrical',
  'data-cabling': 'electrical',
  'cctv-installer': 'security-av',
  'ev-charger': 'solar-ev',
  'solar-pv': 'solar-ev',

  // Heating & Plumbing
  'gas-engineer': 'gas',
  'heating-engineer': 'gas',
  'boiler-installer': 'gas',
  'plumber': 'plumbing',
  'bathroom-fitter': 'plumbing',
  'wet-room': 'plumbing',
  'drainage': 'plumbing',
  'underfloor-heating': 'plumbing',

  // HVAC & Renewables
  'air-con': 'hvac',
  'heat-pump': 'hvac',
  'ventilation': 'hvac',
  'renewable-energy': 'solar-ev',

  // Joinery & Carpentry
  'carpenter': 'joinery',
  'joiner': 'joinery',
  'kitchen-fitter': 'joinery',
  'cabinet-maker': 'joinery',
  'shopfitter': 'joinery',
  'timber-frame': 'joinery',

  // Finishing & Interiors
  'painter-decorator': 'finishing',
  'plasterer': 'finishing',
  'tiler': 'finishing',
  'floor-layer': 'finishing',
  'dryliner': 'finishing',
  'screeder': 'finishing',

  // Roofing & Exteriors
  'roofer': 'roofing',
  'flat-roofer': 'roofing',
  'glazier': 'glazing',
  'window-fitter': 'glazing',
  'fascia-soffit': 'glazing',
  'gutter-specialist': 'glazing',
  'cladding': 'glazing',
  'conservatory': 'glazing',

  // Landscaping & External
  'landscaper': 'landscaping',
  'fencer': 'landscaping',
  'decking': 'landscaping',
  'driveway': 'landscaping',
  'tree-surgeon': 'landscaping',
  'exterior-cleaner': 'exterior-cleaning',

  // Security & Home Technology
  'locksmith': 'security-av',
  'aerial-satellite': 'security-av',
  'smart-home': 'security-av',
  'home-cinema': 'security-av',

  // Specialist & Niche
  'asbestos': 'demolition',
  'pest-control': 'niche',
  'chimney-sweep': 'niche',
  'domestic-cleaner': 'domestic-cleaning',
  'commercial-cleaner': 'commercial-cleaning',
  'swimming-pool': 'niche',
  'steel-fixer': 'demolition',
  'piling': 'demolition',
  'handyman': 'general',
};

/** Resolve a trade string to a preset family key. Unknown trades → 'general'. */
export function resolvePresetFamily(trade: string): PresetFamilyKey {
  return TRADE_PRESET_MAP[trade] || TRADE_PRESET_MAP[trade.toLowerCase()] || 'general';
}

// ---------------------------------------------------------------------------
// Preset families — job type definitions for each family
// ---------------------------------------------------------------------------

export const TRADE_PRESETS: Record<PresetFamilyKey, TradeDefinition> = {
  // defaultCertType on this family is what makes the gas certs skill work out
  // of the box: it tells job detail which certificate to offer, and the certs
  // page which booked jobs still owe one. Every GasCertType except
  // warning_notice is reachable from exactly one job type below — a Gas
  // Warning Notice is auto-drafted off a failing appliance, never booked.
  // src/lib/gas-certs/__tests__/job-type-cert.test.ts enforces that coverage,
  // so a new cert type can't ship unreachable.
  gas: {
    label: 'Gas & Heating',
    types: [
      { name: 'Boiler Service', category: 'Service', defaultDuration: '1 hour', color: 'flame', defaultCertType: 'boiler_service' },
      { name: 'Boiler Repair', category: 'Repair', defaultDuration: '2 hours', color: 'flame-kindling', defaultCertType: 'gas_breakdown_service' },
      { name: 'Boiler Install', category: 'Installation', defaultDuration: '8 hours', color: 'flame', defaultCertType: 'install_commission_decommission' },
      { name: 'CP12 Cert.', category: 'Inspection', defaultDuration: '1 hour', color: 'clipboard-check', defaultCertType: 'cp12' },
      // EICR is electrical, not gas — deliberately unmapped.
      { name: 'EICR Insp.', category: 'Inspection', defaultDuration: '2 hours', color: 'clipboard-check' },
      { name: 'Gas Safety', category: 'Inspection', defaultDuration: '1 hour', color: 'clipboard-check', defaultCertType: 'cp12' },
      { name: 'Annual Service', category: 'Service', defaultDuration: '1.5 hours', color: 'cog', defaultCertType: 'service_maintenance' },
      { name: 'Powerflush', category: 'Service', defaultDuration: '6 hours', color: 'droplets' },
      { name: 'Radiator Install', category: 'Installation', defaultDuration: '3 hours', color: 'thermometer-sun' },
      { name: 'Radiator Fix', category: 'Repair', defaultDuration: '1.5 hours', color: 'wrench' },
      { name: 'Leak Fix', category: 'Repair', defaultDuration: '2 hours', color: 'droplet' },
      { name: 'Cylinder Replace', category: 'Installation', defaultDuration: '4 hours', color: 'flame', defaultCertType: 'unvented_hot_water' },
      { name: 'Valve Replace', category: 'Repair', defaultDuration: '1.5 hours', color: 'pipette' },
      { name: 'Landlord Check', category: 'Inspection', defaultDuration: '1 hour', color: 'clipboard-check', defaultCertType: 'cp12' },
      { name: 'Emrg. Repair', category: 'Repair', defaultDuration: '2 hours', color: 'flame', defaultCertType: 'gas_breakdown_service' },
      // A call-out, not a service visit — deliberately unmapped (TKT-1503).
      { name: 'Service Call', category: 'Service', defaultDuration: '1 hour', color: 'cog' },
      { name: 'Inspection', category: 'Inspection', defaultDuration: '1.5 hours', color: 'clipboard-check' },
      // Appended (not slotted in) so existing accounts' sort_order is undisturbed.
      // These three had no job type at all, leaving their cert types unreachable.
      { name: 'CH Commissioning', category: 'Installation', defaultDuration: '2 hours', color: 'thermometer-sun', defaultCertType: 'central_heating_commissioning' },
      { name: 'Gas Test & Purge', category: 'Service', defaultDuration: '1.5 hours', color: 'flame', defaultCertType: 'gas_testing_purging' },
      { name: 'Catering Insp.', category: 'Inspection', defaultDuration: '2 hours', color: 'clipboard-check', defaultCertType: 'commercial_catering_inspection' },
      // LPG. The name says "LPG" out loud for the same reason the oil types say
      // "Oil": the alias index is global, and a bare "Landlord Check" already
      // belongs to the CP12 above. A job type matching two cert types is worse
      // than one matching none.
      { name: 'LPG Gas Safety', category: 'Inspection', defaultDuration: '1 hour', color: 'clipboard-check', defaultCertType: 'lpg_landlord_safety' },
      // Commercial premises. Two hours rather than one: multi-appliance is the
      // norm on a plant room, which is the whole reason this record exists
      // separately from the CP12.
      { name: 'Commercial Gas Safety', category: 'Inspection', defaultDuration: '2 hours', color: 'clipboard-check', defaultCertType: 'non_domestic_gas_safety' },
      // Water, not gas — it sits in the gas preset family because landlords
      // commission one alongside the annual gas check. The name says
      // "Legionella" out loud because the alias index is global and a bare
      // "Risk Assessment" already belongs to the oil tank one.
      { name: 'Legionella Risk Assessment', category: 'Inspection', defaultDuration: '1 hour', color: 'clipboard-check', defaultCertType: 'legionella_risk_assessment' },
      // Also water. Half a day, because a tank clean and chlorination is a
      // drain-down, a scrub, a dose, a contact time and a flush back.
      { name: 'Chlorination', category: 'Service', defaultDuration: '4 hours', color: 'clipboard-check', defaultCertType: 'water_chlorination' },
      // Oil (OFTEC). The name says "Oil" out loud because the alias index is
      // global: a bare "Annual Service" would collide with the gas job type of
      // that name and start suggesting an OIL certificate for a gas boiler.
      // The remaining OFTEC forms get their job type in the phase that builds
      // them, not before, or the prompt leads to a form that doesn't exist.
      { name: 'Oil Boiler Service', category: 'Service', defaultDuration: '1.5 hours', color: 'flame', defaultCertType: 'oil_service_commissioning' },
      { name: 'Oil Landlord Safety Check', category: 'Inspection', defaultDuration: '1 hour', color: 'clipboard-check', defaultCertType: 'oil_landlord_safety' },
      { name: 'Oil Boiler Install', category: 'Installation', defaultDuration: '8 hours', color: 'flame', defaultCertType: 'oil_installation_completion' },
      { name: 'Oil Tank Risk Assessment', category: 'Inspection', defaultDuration: '1 hour', color: 'clipboard-check', defaultCertType: 'oil_tank_risk_assessment' },
    ],
  },

  plumbing: {
    label: 'Plumbing',
    types: [
      { name: 'Bathroom Install', category: 'Installation', defaultDuration: '8 hours', color: 'bath' },
      { name: 'Kitchen Plumbing', category: 'Installation', defaultDuration: '6 hours', color: 'home' },
      { name: 'Leak Repair', category: 'Repair', defaultDuration: '2 hours', color: 'droplet' },
      { name: 'Drain Clearing', category: 'Service', defaultDuration: '1.5 hours', color: 'droplets' },
      { name: 'Pipe Replacement', category: 'Repair', defaultDuration: '4 hours', color: 'pipette' },
      { name: 'Toilet Install', category: 'Installation', defaultDuration: '3 hours', color: 'toilet' },
      { name: 'Shower Install', category: 'Installation', defaultDuration: '4 hours', color: 'shower-head' },
      { name: 'Tap Replacement', category: 'Repair', defaultDuration: '1 hour', color: 'wrench' },
      { name: 'Water Heater', category: 'Installation', defaultDuration: '6 hours', color: 'thermometer-sun' },
      { name: 'Stopcock Repair', category: 'Repair', defaultDuration: '1.5 hours', color: 'pipette' },
      { name: 'Emrg. Callout', category: 'Repair', defaultDuration: '2 hours', color: 'droplet' },
      { name: 'Plumbing Insp.', category: 'Inspection', defaultDuration: '1.5 hours', color: 'clipboard-check' },
    ],
  },

  electrical: {
    label: 'Electrical',
    types: [
      { name: 'Full Rewire', category: 'Installation', defaultDuration: '8 hours', color: 'cable' },
      { name: 'Partial Rewire', category: 'Installation', defaultDuration: '6 hours', color: 'cable' },
      { name: 'Consumer Unit', category: 'Installation', defaultDuration: '4 hours', color: 'plug' },
      { name: 'EICR', category: 'Inspection', defaultDuration: '3 hours', color: 'clipboard-check' },
      { name: 'Socket Install', category: 'Installation', defaultDuration: '1.5 hours', color: 'plug' },
      { name: 'Light Fitting', category: 'Installation', defaultDuration: '1 hour', color: 'lightbulb' },
      { name: 'Fault Finding', category: 'Repair', defaultDuration: '2 hours', color: 'zap' },
      { name: 'Fuse Board', category: 'Repair', defaultDuration: '3 hours', color: 'plug' },
      { name: 'EV Charger', category: 'Installation', defaultDuration: '4 hours', color: 'plug' },
      { name: 'Fire Alarm', category: 'Installation', defaultDuration: '3 hours', color: 'shield' },
      { name: 'PAT Testing', category: 'Inspection', defaultDuration: '2 hours', color: 'clipboard-check' },
      { name: 'Emrg. Repair', category: 'Repair', defaultDuration: '2 hours', color: 'zap' },
    ],
  },

  // The AC job types carry the F-Gas cert mappings: an install produces the
  // commissioning certificate, a service or repair visit the service record,
  // and refrigerant work the statutory leak check record (which is also where
  // gas added gets logged — F-Gas record-keeping wants the top-up on paper).
  hvac: {
    label: 'HVAC',
    types: [
      { name: 'AC Install', category: 'Installation', defaultDuration: '8 hours', color: 'snowflake', defaultCertType: 'ac_commissioning' },
      { name: 'AC Service', category: 'Service', defaultDuration: '2 hours', color: 'cog', defaultCertType: 'ac_service' },
      { name: 'AC Repair', category: 'Repair', defaultDuration: '3 hours', color: 'wrench', defaultCertType: 'ac_service' },
      { name: 'Duct Cleaning', category: 'Service', defaultDuration: '4 hours', color: 'wind' },
      { name: 'Heat Pump Install', category: 'Installation', defaultDuration: '8 hours', color: 'thermometer-snowflake', defaultCertType: 'hp_commissioning' },
      { name: 'Heat Pump Service', category: 'Service', defaultDuration: '2 hours', color: 'cog', defaultCertType: 'hp_service' },
      { name: 'Ventilation', category: 'Installation', defaultDuration: '6 hours', color: 'wind' },
      { name: 'Thermostat', category: 'Installation', defaultDuration: '1.5 hours', color: 'thermometer-sun' },
      { name: 'Refrigerant Top-Up', category: 'Service', defaultDuration: '1.5 hours', color: 'droplet', defaultCertType: 'fgas_leak_check' },
      { name: 'Emrg. Callout', category: 'Repair', defaultDuration: '2 hours', color: 'snowflake' },
      // Appended (not slotted in) so existing accounts' sort_order is
      // undisturbed — the same rule as the gas family above.
      { name: 'F-Gas Leak Check', category: 'Inspection', defaultDuration: '1 hour', color: 'clipboard-check', defaultCertType: 'fgas_leak_check' },
      { name: 'AC Pressure Test', category: 'Inspection', defaultDuration: '1.5 hours', color: 'clipboard-check', defaultCertType: 'ac_pressure_test' },
      { name: 'Refrigerant Evacuation', category: 'Service', defaultDuration: '1.5 hours', color: 'wind', defaultCertType: 'ac_evacuation' },
      // The installer's readings-led annual service (TKT-1486). Its own job
      // type rather than a second cert on "Heat Pump Service", which stays on
      // the checklist the landlord-facing firms already use.
      { name: 'Heat Pump Performance Service', category: 'Service', defaultDuration: '2.5 hours', color: 'cog', defaultCertType: 'hp_performance' },
    ],
  },

  general: {
    label: 'General Trades',
    types: [
      { name: 'Installation', category: 'Installation', defaultDuration: '4 hours', color: 'hammer' },
      { name: 'Repair', category: 'Repair', defaultDuration: '2 hours', color: 'wrench' },
      { name: 'Inspection', category: 'Inspection', defaultDuration: '1.5 hours', color: 'clipboard-check' },
      { name: 'Maintenance', category: 'Service', defaultDuration: '2 hours', color: 'cog' },
      { name: 'Emrg. Callout', category: 'Repair', defaultDuration: '2 hours', color: 'flame' },
      { name: 'Consultation', category: 'Service', defaultDuration: '1 hour', color: 'briefcase' },
      { name: 'Follow-Up', category: 'Service', defaultDuration: '1 hour', color: 'clock' },
      { name: 'Other', category: 'General', defaultDuration: '2 hours', color: 'settings' },
    ],
  },

  'solar-ev': {
    label: 'Solar & EV',
    types: [
      { name: 'Solar Panel Install', category: 'Installation', defaultDuration: '8 hours', color: 'sun' },
      { name: 'Solar Panel Service', category: 'Service', defaultDuration: '2 hours', color: 'cog' },
      { name: 'EV Charger Install', category: 'Installation', defaultDuration: '4 hours', color: 'plug' },
      { name: 'Battery Storage', category: 'Installation', defaultDuration: '6 hours', color: 'battery' },
      { name: 'Inverter Replace', category: 'Repair', defaultDuration: '3 hours', color: 'zap' },
      { name: 'System Inspection', category: 'Inspection', defaultDuration: '2 hours', color: 'clipboard-check' },
      { name: 'Panel Cleaning', category: 'Service', defaultDuration: '2 hours', color: 'spray-can' },
      { name: 'Emrg. Repair', category: 'Repair', defaultDuration: '2 hours', color: 'zap' },
    ],
  },

  building: {
    label: 'Building & Construction',
    types: [
      { name: 'Extension Build', category: 'Installation', defaultDuration: '8 hours', color: 'construction' },
      { name: 'Loft Conversion', category: 'Installation', defaultDuration: '8 hours', color: 'construction' },
      { name: 'Brickwork', category: 'Installation', defaultDuration: '8 hours', color: 'construction' },
      { name: 'Foundations', category: 'Installation', defaultDuration: '8 hours', color: 'shovel' },
      { name: 'Structural Repair', category: 'Repair', defaultDuration: '6 hours', color: 'hammer' },
      { name: 'Wall Build', category: 'Installation', defaultDuration: '6 hours', color: 'construction' },
      { name: 'Groundwork', category: 'Installation', defaultDuration: '8 hours', color: 'shovel' },
      { name: 'Drainage Install', category: 'Installation', defaultDuration: '6 hours', color: 'pipette' },
      { name: 'Site Survey', category: 'Inspection', defaultDuration: '2 hours', color: 'clipboard-check' },
      { name: 'Emrg. Repair', category: 'Repair', defaultDuration: '3 hours', color: 'hard-hat' },
    ],
  },

  roofing: {
    label: 'Roofing',
    types: [
      { name: 'Roof Replacement', category: 'Installation', defaultDuration: '8 hours', color: 'house' },
      { name: 'Roof Repair', category: 'Repair', defaultDuration: '4 hours', color: 'hammer' },
      { name: 'Flat Roof Install', category: 'Installation', defaultDuration: '8 hours', color: 'house' },
      { name: 'Flat Roof Repair', category: 'Repair', defaultDuration: '3 hours', color: 'hammer' },
      { name: 'Tile Replace', category: 'Repair', defaultDuration: '2 hours', color: 'hammer' },
      { name: 'Lead Work', category: 'Installation', defaultDuration: '6 hours', color: 'house' },
      { name: 'Roof Inspection', category: 'Inspection', defaultDuration: '1.5 hours', color: 'clipboard-check' },
      { name: 'Emrg. Leak Repair', category: 'Repair', defaultDuration: '2 hours', color: 'droplet' },
    ],
  },

  joinery: {
    label: 'Joinery & Carpentry',
    types: [
      { name: 'Kitchen Install', category: 'Installation', defaultDuration: '8 hours', color: 'home' },
      { name: 'Fitted Wardrobe', category: 'Installation', defaultDuration: '8 hours', color: 'home' },
      { name: 'Door Hanging', category: 'Installation', defaultDuration: '2 hours', color: 'key' },
      { name: 'Staircase Install', category: 'Installation', defaultDuration: '8 hours', color: 'construction' },
      { name: 'Skirting & Architrave', category: 'Installation', defaultDuration: '4 hours', color: 'ruler' },
      { name: 'Shelving & Storage', category: 'Installation', defaultDuration: '3 hours', color: 'box' },
      { name: 'Timber Repair', category: 'Repair', defaultDuration: '3 hours', color: 'axe' },
      { name: 'Bespoke Cabinet', category: 'Installation', defaultDuration: '8 hours', color: 'box' },
      { name: 'Shopfit', category: 'Installation', defaultDuration: '8 hours', color: 'home' },
      { name: 'Site Measure', category: 'Inspection', defaultDuration: '1.5 hours', color: 'ruler' },
    ],
  },

  finishing: {
    label: 'Finishing & Interiors',
    types: [
      { name: 'Full Room Paint', category: 'Service', defaultDuration: '8 hours', color: 'paint-roller' },
      { name: 'Exterior Paint', category: 'Service', defaultDuration: '8 hours', color: 'paint-roller' },
      { name: 'Wallpapering', category: 'Service', defaultDuration: '6 hours', color: 'paint-bucket' },
      { name: 'Plastering', category: 'Installation', defaultDuration: '6 hours', color: 'brush' },
      { name: 'Skimming', category: 'Installation', defaultDuration: '4 hours', color: 'brush' },
      { name: 'Wall Tiling', category: 'Installation', defaultDuration: '6 hours', color: 'ruler' },
      { name: 'Floor Tiling', category: 'Installation', defaultDuration: '6 hours', color: 'ruler' },
      { name: 'Floor Laying', category: 'Installation', defaultDuration: '6 hours', color: 'ruler' },
      { name: 'Drylining', category: 'Installation', defaultDuration: '6 hours', color: 'brush' },
      { name: 'Screeding', category: 'Installation', defaultDuration: '4 hours', color: 'ruler' },
    ],
  },

  glazing: {
    label: 'Glazing & Exteriors',
    types: [
      { name: 'Window Install', category: 'Installation', defaultDuration: '4 hours', color: 'home' },
      { name: 'Door Install', category: 'Installation', defaultDuration: '4 hours', color: 'home' },
      { name: 'Double Glazing', category: 'Installation', defaultDuration: '6 hours', color: 'home' },
      { name: 'Glass Replacement', category: 'Repair', defaultDuration: '2 hours', color: 'wrench' },
      { name: 'Fascia & Soffit', category: 'Installation', defaultDuration: '6 hours', color: 'construction' },
      { name: 'Gutter Install', category: 'Installation', defaultDuration: '4 hours', color: 'droplets' },
      { name: 'Gutter Repair', category: 'Repair', defaultDuration: '2 hours', color: 'droplets' },
      { name: 'Conservatory Build', category: 'Installation', defaultDuration: '8 hours', color: 'house' },
      { name: 'Cladding Install', category: 'Installation', defaultDuration: '8 hours', color: 'construction' },
      { name: 'Survey', category: 'Inspection', defaultDuration: '1.5 hours', color: 'clipboard-check' },
    ],
  },

  landscaping: {
    label: 'Landscaping & External',
    types: [
      { name: 'Garden Design', category: 'Service', defaultDuration: '4 hours', color: 'flower' },
      { name: 'Patio Laying', category: 'Installation', defaultDuration: '8 hours', color: 'shovel' },
      { name: 'Fencing Install', category: 'Installation', defaultDuration: '6 hours', color: 'ruler' },
      { name: 'Decking Install', category: 'Installation', defaultDuration: '8 hours', color: 'axe' },
      { name: 'Driveway Paving', category: 'Installation', defaultDuration: '8 hours', color: 'shovel' },
      { name: 'Turfing', category: 'Installation', defaultDuration: '4 hours', color: 'sprout' },
      { name: 'Tree Surgery', category: 'Service', defaultDuration: '4 hours', color: 'tree' },
      { name: 'Fence Repair', category: 'Repair', defaultDuration: '3 hours', color: 'ruler' },
    ],
  },

  demolition: {
    label: 'Demolition & Structural',
    types: [
      { name: 'Demolition', category: 'Service', defaultDuration: '8 hours', color: 'hammer' },
      { name: 'Strip Out', category: 'Service', defaultDuration: '6 hours', color: 'hammer' },
      { name: 'Scaffolding Erect', category: 'Installation', defaultDuration: '6 hours', color: 'construction' },
      { name: 'Scaffolding Strike', category: 'Service', defaultDuration: '4 hours', color: 'construction' },
      { name: 'Asbestos Survey', category: 'Inspection', defaultDuration: '3 hours', color: 'clipboard-check' },
      { name: 'Asbestos Removal', category: 'Service', defaultDuration: '8 hours', color: 'shield' },
      { name: 'Steel Fixing', category: 'Installation', defaultDuration: '8 hours', color: 'forklift' },
      { name: 'Piling', category: 'Installation', defaultDuration: '8 hours', color: 'forklift' },
    ],
  },

  'security-av': {
    label: 'Security & Home Technology',
    types: [
      { name: 'Lock Change', category: 'Service', defaultDuration: '1 hour', color: 'key' },
      { name: 'Lock Repair', category: 'Repair', defaultDuration: '1.5 hours', color: 'key' },
      { name: 'CCTV Install', category: 'Installation', defaultDuration: '4 hours', color: 'camera' },
      { name: 'Alarm Install', category: 'Installation', defaultDuration: '4 hours', color: 'shield' },
      { name: 'Aerial Install', category: 'Installation', defaultDuration: '2 hours', color: 'wifi' },
      { name: 'Smart Home Setup', category: 'Installation', defaultDuration: '4 hours', color: 'wifi' },
      { name: 'Home Cinema', category: 'Installation', defaultDuration: '6 hours', color: 'camera' },
      { name: 'Emrg. Lockout', category: 'Repair', defaultDuration: '1 hour', color: 'key' },
    ],
  },

  niche: {
    label: 'Specialist Trades',
    types: [
      { name: 'Pest Treatment', category: 'Service', defaultDuration: '2 hours', color: 'spray-can' },
      { name: 'Pest Survey', category: 'Inspection', defaultDuration: '1 hour', color: 'spray-can' },
      { name: 'Chimney Sweep', category: 'Service', defaultDuration: '1.5 hours', color: 'flame-kindling' },
      { name: 'Chimney Repair', category: 'Repair', defaultDuration: '4 hours', color: 'flame-kindling' },
      { name: 'Pool Service', category: 'Service', defaultDuration: '3 hours', color: 'waves' },
      { name: 'Pool Repair', category: 'Repair', defaultDuration: '4 hours', color: 'waves' },
    ],
  },

  // Exterior cleaning is its own family rather than a landscaping alias: the
  // work is repeat cleaning visits (window rounds, softwash, gutter clears),
  // not the install-and-leave jobs the landscaping presets seed.
  'exterior-cleaning': {
    label: 'Exterior Cleaning',
    types: [
      { name: 'Window Clean', category: 'Service', defaultDuration: '1 hour', color: 'droplets' },
      { name: 'Gutter Clear', category: 'Service', defaultDuration: '1.5 hours', color: 'droplet' },
      { name: 'Pressure Wash', category: 'Service', defaultDuration: '3 hours', color: 'spray-can' },
      { name: 'Driveway Clean', category: 'Service', defaultDuration: '4 hours', color: 'spray-can' },
      { name: 'Roof Clean', category: 'Service', defaultDuration: '6 hours', color: 'house' },
      { name: 'Render Softwash', category: 'Service', defaultDuration: '5 hours', color: 'waves' },
      { name: 'Conservatory Clean', category: 'Service', defaultDuration: '2 hours', color: 'home' },
      { name: 'Solar Panel Clean', category: 'Service', defaultDuration: '2 hours', color: 'sun' },
      { name: 'Fascia & Soffit Clean', category: 'Service', defaultDuration: '2 hours', color: 'brush' },
      { name: 'Site Survey', category: 'Inspection', defaultDuration: '1 hour', color: 'clipboard-check' },
    ],
  },

  // Cleaning gets its own families for the same reason exterior cleaning does
  // ('niche' would seed a cleaner with Pest Treatment and Pool Repair), and two
  // of them because the trades quote differently: domestic per hour or per
  // bedroom, commercial on a monthly contract.
  'domestic-cleaning': {
    label: 'Domestic Cleaning',
    types: [
      { name: 'Regular Clean', category: 'Service', defaultDuration: '2 hours', color: 'spray-can' },
      { name: 'Deep Clean', category: 'Service', defaultDuration: '5 hours', color: 'spray-can' },
      { name: 'End of Tenancy Clean', category: 'Service', defaultDuration: '6 hours', color: 'key' },
      { name: 'Oven Clean', category: 'Service', defaultDuration: '2 hours', color: 'flame' },
      { name: 'Carpet Clean', category: 'Service', defaultDuration: '3 hours', color: 'droplets' },
      { name: 'Internal Window Clean', category: 'Service', defaultDuration: '1 hour', color: 'home' },
    ],
  },

  'commercial-cleaning': {
    label: 'Commercial Cleaning',
    types: [
      { name: 'Office Clean', category: 'Service', defaultDuration: '3 hours', color: 'briefcase' },
      { name: 'Contract Visit', category: 'Service', defaultDuration: '2 hours', color: 'calendar' },
      { name: 'Communal Areas Clean', category: 'Service', defaultDuration: '2 hours', color: 'house' },
      { name: 'Washroom Service', category: 'Service', defaultDuration: '1 hour', color: 'toilet' },
      { name: 'After Builders Clean', category: 'Service', defaultDuration: '8 hours', color: 'hard-hat' },
      { name: 'Commercial Kitchen Clean', category: 'Service', defaultDuration: '5 hours', color: 'flame' },
    ],
  },
};
